import React, {
    ForwardedRef,
    forwardRef,
    memo,
    ReactNode,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
} from 'react';
import clsx from 'clsx';
import styles from './Drawer.module.scss';

type DrawerProps = {
    open: boolean;
    children: ReactNode;
    onClose?: () => void;
    isInfo?: boolean;
    isFilterPanel?: boolean;
    enableVerticalSlideMobile?: boolean;
    highZIndex?: boolean;
    roundedCorners?: 'left' | 'top';
    ariaTitle?: string;
    id?: string;
    // Set when content includes a virtualized list, where the "last focusable element" can't be reliably found.
    hasVirtualizedContent?: boolean;
    className?: string;
};

const DrawerComponent = (
    {
        children,
        open,
        onClose,
        isInfo,
        isFilterPanel,
        enableVerticalSlideMobile,
        highZIndex,
        ariaTitle,
        roundedCorners,
        id,
        hasVirtualizedContent,
        className,
    }: DrawerProps,
    forwardedRef: ForwardedRef<HTMLDivElement | null>,
) => {
    const previousFocus = useRef<HTMLElement | null>(null);
    const drawerRef = useRef<HTMLDivElement>(null);

    useImperativeHandle<HTMLDivElement | null, HTMLDivElement | null>(
        forwardedRef,
        () => drawerRef.current,
        [],
    );

    const getFocusableElements = () =>
        drawerRef.current?.querySelectorAll<HTMLElement>(
            'button:not([disabled]), [href], input:not([disabled]), [tabindex="0"]:not([data-drawer-sentinel])',
        );

    const focusOnFirstElement = () => {
        const elements = getFocusableElements();

        if (elements?.length) {
            elements[0].focus();
        }
    };

    const focusOnLastElement = () => {
        const elements = getFocusableElements();

        if (elements?.length) {
            elements[elements.length - 1].focus();
        }
    };

    useMemo(() => {
        if (open) {
            previousFocus.current = document.activeElement as HTMLElement;

            setTimeout(() => {
                focusOnFirstElement();
            }, 300);
        } else if (!open && previousFocus.current) {
            previousFocus.current.focus();
        }
    }, [open]);

    useEffect(() => {
        return () => previousFocus.current?.focus();
    }, []);

    const onKeyDownHandler = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Escape') {
                onClose?.();
            }
        },
        [onClose],
    );

    return (
        <div
            className={clsx(styles.drawerContainer, {
                [styles.open]: open,
            })}
            onKeyDown={onKeyDownHandler}
        >
            <div
                id={id}
                ref={drawerRef}
                tabIndex={0}
                role="dialog"
                aria-modal="true"
                aria-label={ariaTitle}
                className={clsx(styles.drawer, className, {
                    [styles.drawerOpen]: open,
                    [styles.info]: isInfo,
                    [styles.filterPanel]: isFilterPanel,
                    [styles.verticalMobile]: enableVerticalSlideMobile,
                    [styles.highZIndex]: highZIndex,
                    [styles.roundedLeft]: roundedCorners === 'left',
                    [styles.roundedTop]: roundedCorners === 'top',
                })}
            >
                <div
                    className={clsx(styles.content, {
                        [styles.contentRoundedLeft]: roundedCorners === 'left',
                        [styles.contentRoundedTop]: roundedCorners === 'top',
                    })}
                >
                    <span
                        data-drawer-sentinel
                        tabIndex={0}
                        onFocus={hasVirtualizedContent ? focusOnFirstElement : focusOnLastElement}
                    />

                    {children}

                    <span data-drawer-sentinel tabIndex={0} onFocus={focusOnFirstElement} />
                </div>
            </div>

            <div
                className={clsx(styles.backdrop, {
                    [styles.backdropOpen]: open,
                    [styles.backdropBlur]: isInfo || enableVerticalSlideMobile,
                    [styles.backdropSolid]: isFilterPanel,
                    [styles.backdropHighZIndex]: highZIndex,
                })}
                onClick={onClose}
            />
        </div>
    );
};

export const Drawer = memo(forwardRef(DrawerComponent));

Drawer.displayName = 'Drawer';
